/**
 * Workspace Details Redux Slice
 *
 * Manages detailed workspace data (groups, links, notes) with
 * normalized structure and per-workspace caching.
 *
 * Features:
 * - Normalized nested data structure
 * - Per-workspace loading and error states
 * - Optimistic updates for better UX
 * - Selective data updates
 */

import {
  createSlice,
  createSelector,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type {
  NormalizedGroup,
  NormalizedTile,
  Note,
  TileId,
} from "../../types/entities";

/** The kinds of data a workspace's details hold. */
export type DetailsType = "groups" | "links" | "notes";

/** A link in the details: a normalized tile with its id resolved. */
export type DetailsLink = NormalizedTile & { id: TileId };

/** One workspace's details, normalized. */
export interface WorkspaceDetailsEntry {
  groups: Record<number, NormalizedGroup>;
  links: Record<TileId, DetailsLink>;
  notes: Record<number, Note>;
  groupIds: number[];
  linkIds: TileId[];
  noteIds: number[];
  lastFetch: number | null;
  loading: Record<DetailsType, boolean>;
  errors: Record<DetailsType, string | null>;
}

export interface WorkspaceDetailsState {
  byWorkspaceId: Record<number, WorkspaceDetailsEntry>;
  loading: Record<number, boolean>;
  errors: Record<number, string | null>;
  meta: {
    lastCleanup: number | null;
    totalCached: number;
  };
}

/** The state selectors read: any store with this slice. */
interface StateWithDetails {
  workspaceDetails: WorkspaceDetailsState;
}

const emptyDetails = (): WorkspaceDetailsEntry => ({
  groups: {},
  links: {},
  notes: {},
  groupIds: [],
  linkIds: [],
  noteIds: [],
  lastFetch: null,
  loading: { groups: false, links: false, notes: false },
  errors: { groups: null, links: null, notes: null },
});

const initialState: WorkspaceDetailsState = {
  // Normalized details by workspace ID
  byWorkspaceId: {
    // workspaceId: {
    //   groups: { groupId: {...} },
    //   links: { linkId: {...} },
    //   notes: { noteId: {...} },
    //   groupIds: [],
    //   linkIds: [],
    //   noteIds: [],
    //   lastFetch: timestamp,
    //   loading: { groups: false, links: false, notes: false },
    //   errors: { groups: null, links: null, notes: null }
    // }
  },

  // Global loading states
  loading: {}, // { workspaceId: boolean }

  // Global error states
  errors: {}, // { workspaceId: error }

  // Cache metadata
  meta: {
    lastCleanup: null,
    totalCached: 0,
  },
};

const workspaceDetailsSlice = createSlice({
  name: "workspaceDetails",
  initialState,
  reducers: {
    // Set complete details for a workspace
    setDetails: (
      state,
      action: PayloadAction<{
        workspaceId: number;
        groups?: NormalizedGroup[];
        links?: DetailsLink[];
        notes?: Note[];
        lastFetch?: number;
      }>,
    ) => {
      const {
        workspaceId,
        groups = [],
        links = [],
        notes = [],
        lastFetch,
      } = action.payload;

      // Initialize workspace details if not exists
      if (!state.byWorkspaceId[workspaceId]) {
        state.byWorkspaceId[workspaceId] = emptyDetails();
      }

      const workspace = state.byWorkspaceId[workspaceId];

      // Normalize and store groups
      workspace.groups = {};
      workspace.groupIds = [];
      groups.forEach((group) => {
        workspace.groups[group.id] = group;
        workspace.groupIds.push(group.id);
      });

      // Normalize and store links
      workspace.links = {};
      workspace.linkIds = [];
      links.forEach((link) => {
        workspace.links[link.id] = link;
        workspace.linkIds.push(link.id);
      });

      // Normalize and store notes
      workspace.notes = {};
      workspace.noteIds = [];
      notes.forEach((note) => {
        workspace.notes[note.id] = note;
        workspace.noteIds.push(note.id);
      });

      workspace.lastFetch = lastFetch || Date.now();

      // Update metadata
      state.meta.totalCached = Object.keys(state.byWorkspaceId).length;
    },

    // Update groups for a workspace
    updateGroups: (
      state,
      action: PayloadAction<{ workspaceId: number; groups: NormalizedGroup[] }>,
    ) => {
      const { workspaceId, groups } = action.payload;

      if (!state.byWorkspaceId[workspaceId]) {
        state.byWorkspaceId[workspaceId] = emptyDetails();
      }

      const workspace = state.byWorkspaceId[workspaceId];

      workspace.groups = {};
      workspace.groupIds = [];
      groups.forEach((group) => {
        workspace.groups[group.id] = group;
        workspace.groupIds.push(group.id);
      });

      workspace.lastFetch = Date.now();
    },

    // Update links for a workspace
    updateLinks: (
      state,
      action: PayloadAction<{ workspaceId: number; links: DetailsLink[] }>,
    ) => {
      const { workspaceId, links } = action.payload;

      if (!state.byWorkspaceId[workspaceId]) {
        state.byWorkspaceId[workspaceId] = emptyDetails();
      }

      const workspace = state.byWorkspaceId[workspaceId];

      workspace.links = {};
      workspace.linkIds = [];
      links.forEach((link) => {
        workspace.links[link.id] = link;
        workspace.linkIds.push(link.id);
      });

      workspace.lastFetch = Date.now();
    },

    // Update notes for a workspace
    updateNotes: (
      state,
      action: PayloadAction<{ workspaceId: number; notes: Note[] }>,
    ) => {
      const { workspaceId, notes } = action.payload;

      if (!state.byWorkspaceId[workspaceId]) {
        state.byWorkspaceId[workspaceId] = emptyDetails();
      }

      const workspace = state.byWorkspaceId[workspaceId];

      workspace.notes = {};
      workspace.noteIds = [];
      notes.forEach((note) => {
        workspace.notes[note.id] = note;
        workspace.noteIds.push(note.id);
      });

      workspace.lastFetch = Date.now();
    },

    // Add a single group
    addGroup: (
      state,
      action: PayloadAction<{ workspaceId: number; group: NormalizedGroup }>,
    ) => {
      const { workspaceId, group } = action.payload;

      if (state.byWorkspaceId[workspaceId]) {
        const workspace = state.byWorkspaceId[workspaceId];
        workspace.groups[group.id] = group;
        if (!workspace.groupIds.includes(group.id)) {
          workspace.groupIds.push(group.id);
        }
      }
    },

    // Update a single group
    updateGroup: (
      state,
      action: PayloadAction<{
        workspaceId: number;
        groupId: number;
        updates: Partial<NormalizedGroup>;
      }>,
    ) => {
      const { workspaceId, groupId, updates } = action.payload;

      if (state.byWorkspaceId[workspaceId]?.groups[groupId]) {
        state.byWorkspaceId[workspaceId].groups[groupId] = {
          ...state.byWorkspaceId[workspaceId].groups[groupId],
          ...updates,
        };
      }
    },

    // Remove a group
    removeGroup: (
      state,
      action: PayloadAction<{ workspaceId: number; groupId: number }>,
    ) => {
      const { workspaceId, groupId } = action.payload;

      if (state.byWorkspaceId[workspaceId]) {
        const workspace = state.byWorkspaceId[workspaceId];
        delete workspace.groups[groupId];
        workspace.groupIds = workspace.groupIds.filter((id) => id !== groupId);
      }
    },

    // Add a single link
    addLink: (
      state,
      action: PayloadAction<{ workspaceId: number; link: DetailsLink }>,
    ) => {
      const { workspaceId, link } = action.payload;

      if (state.byWorkspaceId[workspaceId]) {
        const workspace = state.byWorkspaceId[workspaceId];
        workspace.links[link.id] = link;
        if (!workspace.linkIds.includes(link.id)) {
          workspace.linkIds.push(link.id);
        }
      }
    },

    // Update a single link
    updateLink: (
      state,
      action: PayloadAction<{
        workspaceId: number;
        linkId: TileId;
        updates: Partial<DetailsLink>;
      }>,
    ) => {
      const { workspaceId, linkId, updates } = action.payload;

      if (state.byWorkspaceId[workspaceId]?.links[linkId]) {
        state.byWorkspaceId[workspaceId].links[linkId] = {
          ...state.byWorkspaceId[workspaceId].links[linkId],
          ...updates,
        };
      }
    },

    // Remove a link
    removeLink: (
      state,
      action: PayloadAction<{ workspaceId: number; linkId: TileId }>,
    ) => {
      const { workspaceId, linkId } = action.payload;

      if (state.byWorkspaceId[workspaceId]) {
        const workspace = state.byWorkspaceId[workspaceId];
        delete workspace.links[linkId];
        workspace.linkIds = workspace.linkIds.filter((id) => id !== linkId);
      }
    },

    // Add a single note
    addNote: (
      state,
      action: PayloadAction<{ workspaceId: number; note: Note }>,
    ) => {
      const { workspaceId, note } = action.payload;

      if (state.byWorkspaceId[workspaceId]) {
        const workspace = state.byWorkspaceId[workspaceId];
        workspace.notes[note.id] = note;
        if (!workspace.noteIds.includes(note.id)) {
          workspace.noteIds.push(note.id);
        }
      }
    },

    // Update a single note
    updateNote: (
      state,
      action: PayloadAction<{
        workspaceId: number;
        noteId: number;
        updates: Partial<Note>;
      }>,
    ) => {
      const { workspaceId, noteId, updates } = action.payload;

      if (state.byWorkspaceId[workspaceId]?.notes[noteId]) {
        state.byWorkspaceId[workspaceId].notes[noteId] = {
          ...state.byWorkspaceId[workspaceId].notes[noteId],
          ...updates,
        };
      }
    },

    // Remove a note
    removeNote: (
      state,
      action: PayloadAction<{ workspaceId: number; noteId: number }>,
    ) => {
      const { workspaceId, noteId } = action.payload;

      if (state.byWorkspaceId[workspaceId]) {
        const workspace = state.byWorkspaceId[workspaceId];
        delete workspace.notes[noteId];
        workspace.noteIds = workspace.noteIds.filter((id) => id !== noteId);
      }
    },

    // Set loading state
    setLoading: (
      state,
      action: PayloadAction<{ workspaceId: number; loading: boolean }>,
    ) => {
      const { workspaceId, loading } = action.payload;
      state.loading[workspaceId] = loading;
    },

    // Set loading state for specific data type
    setTypeLoading: (
      state,
      action: PayloadAction<{
        workspaceId: number;
        type: DetailsType;
        loading: boolean;
      }>,
    ) => {
      const { workspaceId, type, loading } = action.payload;
      if (state.byWorkspaceId[workspaceId]) {
        state.byWorkspaceId[workspaceId].loading[type] = loading;
      }
    },

    // Set error state
    setError: (
      state,
      action: PayloadAction<{ workspaceId: number; error: string | null }>,
    ) => {
      const { workspaceId, error } = action.payload;
      state.errors[workspaceId] = error;
    },

    // Set error state for specific data type
    setTypeError: (
      state,
      action: PayloadAction<{
        workspaceId: number;
        type: DetailsType;
        error: string | null;
      }>,
    ) => {
      const { workspaceId, type, error } = action.payload;
      if (state.byWorkspaceId[workspaceId]) {
        state.byWorkspaceId[workspaceId].errors[type] = error;
      }
    },

    // Remove all details for a workspace
    removeDetails: (state, action: PayloadAction<number>) => {
      const workspaceId = action.payload;
      delete state.byWorkspaceId[workspaceId];
      delete state.loading[workspaceId];
      delete state.errors[workspaceId];
      state.meta.totalCached = Object.keys(state.byWorkspaceId).length;
    },

    // Clean up old cached data
    cleanupOldCache: (state, action: PayloadAction<number | undefined>) => {
      const maxAge = action.payload || 30 * 60 * 1000; // 30 minutes default
      const now = Date.now();

      Object.keys(state.byWorkspaceId).forEach((key) => {
        const workspaceId = Number(key);
        const details = state.byWorkspaceId[workspaceId];
        if (details.lastFetch && now - details.lastFetch > maxAge) {
          delete state.byWorkspaceId[workspaceId];
          delete state.loading[workspaceId];
          delete state.errors[workspaceId];
        }
      });

      state.meta.lastCleanup = now;
      state.meta.totalCached = Object.keys(state.byWorkspaceId).length;
    },

    // Reset state
    reset: () => initialState,
  },
});

// Export actions
export const workspaceDetailsActions = workspaceDetailsSlice.actions;

// Selectors
const selectDetailsState = (state: StateWithDetails) => state.workspaceDetails;

// Get all details for a workspace
export const selectWorkspaceDetails = createSelector(
  [
    selectDetailsState,
    (_: StateWithDetails, workspaceId: number) => workspaceId,
  ],
  (details, workspaceId): WorkspaceDetailsEntry | null =>
    details.byWorkspaceId[workspaceId] || null,
);

// Get groups for a workspace
export const selectWorkspaceGroups = createSelector(
  [selectWorkspaceDetails],
  (details) => {
    if (!details) return [];
    return details.groupIds.map((id) => details.groups[id]);
  },
);

// Get links for a workspace
export const selectWorkspaceLinks = createSelector(
  [selectWorkspaceDetails],
  (details) => {
    if (!details) return [];
    return details.linkIds.map((id) => details.links[id]);
  },
);

// Get notes for a workspace
export const selectWorkspaceNotes = createSelector(
  [selectWorkspaceDetails],
  (details) => {
    if (!details) return [];
    return details.noteIds.map((id) => details.notes[id]);
  },
);

// Get specific group
export const selectGroup = createSelector(
  [
    selectWorkspaceDetails,
    (_: StateWithDetails, __: number, groupId: number) => groupId,
  ],
  (details, groupId) => details?.groups[groupId] || null,
);

// Get specific link
export const selectLink = createSelector(
  [
    selectWorkspaceDetails,
    (_: StateWithDetails, __: number, linkId: TileId) => linkId,
  ],
  (details, linkId) => details?.links[linkId] || null,
);

// Get specific note
export const selectNote = createSelector(
  [
    selectWorkspaceDetails,
    (_: StateWithDetails, __: number, noteId: number) => noteId,
  ],
  (details, noteId) => details?.notes[noteId] || null,
);

// Loading states
export const selectIsDetailsLoading = createSelector(
  [
    selectDetailsState,
    (_: StateWithDetails, workspaceId: number) => workspaceId,
  ],
  (details, workspaceId) => details.loading[workspaceId] || false,
);

export const selectIsTypeLoading = createSelector(
  [
    selectWorkspaceDetails,
    (_: StateWithDetails, __: number, type: DetailsType) => type,
  ],
  (details, type) => details?.loading[type] || false,
);

// Error states
export const selectDetailsError = createSelector(
  [
    selectDetailsState,
    (_: StateWithDetails, workspaceId: number) => workspaceId,
  ],
  (details, workspaceId) => details.errors[workspaceId] || null,
);

export const selectTypeError = createSelector(
  [
    selectWorkspaceDetails,
    (_: StateWithDetails, __: number, type: DetailsType) => type,
  ],
  (details, type) => details?.errors[type] || null,
);

// Cache validity
export const selectIsDetailsCacheValid = createSelector(
  [selectWorkspaceDetails],
  (details) => {
    if (!details?.lastFetch) return false;
    const cacheTimeout = 2 * 60 * 1000; // 2 minutes
    return Date.now() - details.lastFetch < cacheTimeout;
  },
);

// Meta selectors
export const selectTotalCachedWorkspaces = createSelector(
  [selectDetailsState],
  (details) => details.meta.totalCached,
);

export const selectLastCleanup = createSelector(
  [selectDetailsState],
  (details) => details.meta.lastCleanup,
);

// Combined selector for full workspace data
export const selectFullWorkspaceData = createSelector(
  [selectWorkspaceGroups, selectWorkspaceLinks, selectWorkspaceNotes],
  (groups, links, notes) => ({
    groups,
    links,
    notes,
    isEmpty: groups.length === 0 && links.length === 0 && notes.length === 0,
  }),
);

export default workspaceDetailsSlice.reducer;
